import { Component, EventEmitter, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NoonReportsService, NoonReportV202005} from '../services/noon-reports.service';
import _ from 'lodash';
import { DateRange } from '../../../shared/components/date-range.component';
import { ActivatedRoute, Router } from '@angular/router';
import * as Highcharts from 'highcharts';
import { DateTimeHelpers } from '../../../shared/helpers/date-time.helpers';
import { Subject } from 'rxjs';
import { UserLocalStoreService } from '../../../shared/services/user-local-store.service';
import { MessageLevel, PgMessagesService } from '../../../shared/services/pg-messages.service';
import { saveAs } from 'file-saver';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum, PgTitleComponent
} from '../../../shared/components/title-bar/title-bar.component';
import { VesselReportToolbarComponent } from '../vessel-report-toolbar/vessel-report-toolbar.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { SailingStatusv202005 } from 'src/app/generated/services/transportation/reports/noon';

interface VesselReports {
  vesselName: string;
  vesselImo: string;
  reports: NoonReportV202005[];
}

interface VesselConsumptionPerDay {
  vesselName: string;
  vesselImo: string;
  date: string;
  consumption: Map<string, number>;
}

interface BunkerStatusComponentSettings {
  selectedFuelType: string;
}

@Component({
  selector: 'app-bunker-status',
  templateUrl: './bunker-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule,]
})
export class BunkerStatusComponent implements OnInit, PgTitleComponent, PgDateRangeTitleComponent {
  toolbarComponentType = VesselReportToolbarComponent;

  toolbarComponent!: VesselReportToolbarComponent;

  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();
  _selectedDateRange!: DateRange;
  consumptionPerDay: VesselConsumptionPerDay[] = [];
  reports: NoonReportV202005[] = [];
  _settings!: BunkerStatusComponentSettings;

  get selectedFuelType(): string {
    return this._settings.selectedFuelType;
  }
  set selectedFuelType(value: string) {
    this._settings.selectedFuelType = value;
    this._userLocalStorage.save('BunkerStatusComponent', this._settings);
    this.prepareSummaryReport();
  }

  dtOptions: any;
  isDataTableInitialised = false;
  dtTrigger: Subject<any> = new Subject<any>();
  dataExists: boolean[] = [true, true, true, true, true, true, true, true];

  constructor(private _noonReportService: NoonReportsService,
    private _activatedRoute: ActivatedRoute,
    private _userLocalStorage: UserLocalStoreService,
    private _pgMessagesService: PgMessagesService,
    private _router: Router,
  ) { }

  private static groupNoonReportV202005(reports: NoonReportV202005[]): VesselReports[] {
    return _.chain(reports)
      .groupBy(item => `${item.vesselName}§${item.vesselImo}`)
      .map((items, key) => {
        const vessel = key.split('§')[0];
        const imo = key.split('§')[1];
        return {
          reports: items.sort((a, b) => a.date!.timestamp - b.date!.timestamp),
          vesselName: vessel,
          vesselImo: imo
        };
      }).value();
  }

  ngOnInit() {
    this._settings = this._userLocalStorage.get<BunkerStatusComponentSettings>('BunkerStatusComponent');
    if (!this._settings) {
      this._settings = { selectedFuelType: 'lsfo', };
    }
    this.title.emit('Bunker status report');
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this._selectedDateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };

      this.dateRangeSubTitle.emit([this._selectedDateRange, PgDateRangeTitleEnum.defaultTitle]);
      this._noonReportService.getNoonReportsV202005Between(this._selectedDateRange).subscribe(reports => {
        this.reports = reports;
        this.plotConsumption(BunkerStatusComponent.groupNoonReportV202005(reports));
        this.prepareSummaryReport();
      });
    });

    this.getParameters();
    this.toolbarComponent.date.subscribe(dateRange => {
      this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this._router
          .navigate(['/vessels/bunker'], {
            queryParams: {
              startTime: dateRange?.startDate.getTime(),
              endTime: dateRange?.endDate.getTime()
            }
          })
          .then((e) => console.log(`Result of Navigation is ${e}`));
      });
    });
  }

  getParameters() {
    this._activatedRoute.queryParams.subscribe(p => {
      const startDate = +p['startTime'];
      const endDate = +p['endTime'];
      this._selectedDateRange = {
        startDate: new Date(startDate),
        endDate: new Date(endDate)
      };
      this.toolbarComponent.dateRange = this._selectedDateRange;
      this.title.emit('Bunker Status Report');
      this.dateRangeSubTitle.emit([this._selectedDateRange, PgDateRangeTitleEnum.defaultTitle]);
    });
  }

  downloadConsumptions() {
    this._noonReportService.downloadConsumptionsReport(this._selectedDateRange.startDate.getTime(),
      this._selectedDateRange.endDate.getTime()).subscribe(b => {
      saveAs(b.body || '', `${DateTimeHelpers.formatLocalDate(this._selectedDateRange.startDate.getTime())}-to-` +
          `${DateTimeHelpers.formatLocalDate(this._selectedDateRange.endDate.getTime())}-consumptions.xlsx`);
    }, () => {
      this._pgMessagesService.publishMessage({
        level: MessageLevel.Error,
        message: 'Could not download bunker consumption report',
        topic: 'Voyages report'
      });
    });

  }

  totalBunkerConsumption(reports: NoonReportV202005[]) {
    const d = reports.map(n =>
      n.noonReportBunkerStatus?.sailingBunkerConsumption.map(i => i.bunkerConsumption)
        .concat(n.noonReportBunkerStatus.otherBunkerConsumption.map(i => i.bunkerConsumption))
        .flatMap(i => i)
    ).flatMap(i => i);
    return _.chain(d).groupBy(b => b?.bunkerType)
      .map((v, k) => (
        {
          bunkerType: k,
          quantityMt: v.map(i => i?.quantityMt).reduce((x, y) => (x || 0) + (y || 0), 0)
        }
      )).value();
  }

  plotConsumption(data: VesselReports[]) {
    const d = data.map(i => (
      {
        vesselName: i.vesselName,
        bunker: this.totalBunkerConsumption(i.reports)
      }
    ));
    const chartSeries = _.chain(d).map(n => n.bunker).flatMap(i => i).groupBy(i => i.bunkerType)
      .map((v, k) => (
        {
          name: k,
          data: v.map(b => Math.round(b.quantityMt || 0 * 100) / 100)
        }
      )).value();
    Highcharts.chart('bunkerConsumption', {
      chart: { type: 'column' },
      title: { text: 'Fuel consumption' },
      xAxis: { categories: d.map(v => v.vesselName) },
      yAxis: {
        min: 0,
        title: {
          text: 'Total fuel consumption'
        },
        stackLabels: {
          enabled: true,
        }
      },
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 25,
        floating: true,
        backgroundColor: Highcharts.defaultOptions.legend!.backgroundColor || 'white',
        borderColor: '#CCC',
        borderWidth: 1,
        shadow: false
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true
          }
        }
      },
      series: [{
        type: 'column',
        data: chartSeries
      }],
    });
  }

  getSailingConsumptionPerSailingStatus(reports: NoonReportV202005[], status: SailingStatusv202005) {
    return reports.flatMap(r => r.noonReportBunkerStatus?.sailingBunkerConsumption.filter(s => s.status === status))
      .flatMap(s => s?.bunkerConsumption.map(b => b.quantityMt)).reduce((i, j) => (i || 0) + (j || 0), 0) || 0;
  }

  getOtherConsumption(reports: NoonReportV202005[], part: 'boilders' | 'powerGenerators') {
    return reports.flatMap(r => r.noonReportBunkerStatus?.otherBunkerConsumption.filter(s => s.partName === part))
      .flatMap(s => s?.bunkerConsumption.map(b => b.quantityMt)).reduce((i, j) => (i || 0) + (j || 0), 0) || 0;
  }

  groupConsumptions(reports: NoonReportV202005[]): Map<string, number> {
    const consumptions = new Map<string, number>();
    consumptions.set('ballastSteaming', this.getSailingConsumptionPerSailingStatus(reports, SailingStatusv202005.SAILING_STATUS_BALLAST_STEAMING));
    consumptions.set('ballastManeuvering', this.getSailingConsumptionPerSailingStatus(reports, SailingStatusv202005.SAILING_STATUS_BALLAST_MANEUVERING));
    consumptions.set('ladenSteaming', this.getSailingConsumptionPerSailingStatus(reports, SailingStatusv202005.SAILING_STATUS_LADEN_STEAMING));
    consumptions.set('ladenManeuvering', this.getSailingConsumptionPerSailingStatus(reports, SailingStatusv202005.SAILING_STATUS_LADEN_MANEUVERING));
    consumptions.set('drifting', this.getSailingConsumptionPerSailingStatus(reports, SailingStatusv202005.SAILING_STATUS_DRIFTING));
    consumptions.set('boilers', this.getOtherConsumption(reports, 'boilders'));
    consumptions.set('powerGenerators', this.getOtherConsumption(reports, 'powerGenerators'));
    consumptions.set('total', 0);
    consumptions.set('ME distance', 0);

    return consumptions;
  }

  prepareSummaryReport() {
    if (this._settings && this._settings.selectedFuelType) {
      this.consumptionPerDay = _.chain(this.reports)
        .groupBy(item => `${item.vesselName}§${item.vesselImo}§${DateTimeHelpers.formatLocalDate(item.date?.timestamp || 0)}`)
        .map((items, key) => {
          const vessel = key.split('§')[0];
          const imo = key.split('§')[1];
          return {
            vesselName: vessel,
            vesselImo: imo,
            date: key.split('§')[2],
            consumption: this.groupConsumptions(items)
          };
        }).value();
    }
  }
}
